// TODO @exclusives to resolve
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* storybook-check-ignore */
import { useState, useMemo, useEffect } from 'react';
import { css } from '@emotion/react';
import { Box, Button, Image, Link } from '@opendoor/bricks/core';
import { DEFAULT_LISTING_IMAGE } from './constants/constants';
import ChevronLeftBold from './icons/ChevronLeftBold';
import PhotoViewer from './PhotoViewer';
import { formatCurrency } from '@opendoor/bricks/helpers/currencyHelpers';
import sortBy from 'lodash/sortBy';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import { ListingPhoto, ListingWithListingStates } from '../../declarations/exclusives/listing';
import { useListing } from './hooks/useListing';
import {
  getIsSellerOfferExperiment,
  getListingAddressHumanReadable,
} from '../../helpers/exclusives/listing';
import { mediaBreakpoints } from 'helpers/exclusives/pdpMediaBreakpoints';
import { REDIRECT_URLS_REGEX } from 'components/globals';
import { isValidRelativeRedirectTo } from 'helpers/exclusives/redirectTo';

const styles = {
  propertyGallery: css`
    width: 100%;
    position: relative;
    box-sizing: border-box;
    @media (min-width: ${mediaBreakpoints.MD}) {
      top: 0;
      flex-shrink: 0;
      position: sticky;
      width: 47%;
      box-shadow: 0px 10px 22px rgba(29, 71, 123, 0.08), 0px 2px 12px rgba(29, 71, 123, 0.08);
    }

    @media (min-width: ${mediaBreakpoints.LG}) {
      width: calc(100% - max(var(--right-side-max-width), min(var(--right-side-max-width), 36%)));
    }

    .property-main-image {
      @media (min-width: ${mediaBreakpoints.MD}) {
        top: 67px;
        position: sticky;
      }
    }
    .property-main-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .linear-gradient-container {
      background: linear-gradient(270deg, #222 0%, rgba(34, 34, 34, 0) 100%);
      bottom: 0;
      cursor: pointer;
      height: 70px;
      position: absolute;
      right: 0;
      width: 50px;
    }
    .linear-gradient-container-left {
      background: linear-gradient(90deg, #222 0%, rgba(34, 34, 34, 0) 100%);
      bottom: 0;
      cursor: pointer;
      height: 70px;
      position: absolute;
      left: 0;
      width: 50px;
    }
  `,
};

interface PropertyGalleryProps {
  onSelfTourClick: () => void;
}

const PropertyGallery = ({ onSelfTourClick }: PropertyGalleryProps) => {
  const router = useRouter();
  const listing = useListing() as ListingWithListingStates;
  const [returnHref, setReturnHref] = useState(
    `/exclusives/gallery/${listing.address.market.identifier}`,
  );
  const isSellerOfferExperiment = getIsSellerOfferExperiment(listing);
  const canShowPhotos = !!listing.listing_photos.length;
  const canShowMorePhotos = canShowPhotos && listing.listing_photos.length > 1;
  const hasListingPhotos =
    canShowPhotos && !!listing.listing_photos[0]?.thumbnail_urls?.['_760x480'];
  const photos = useMemo<ListingPhoto[]>(() => {
    // Sorting array to assure it's stable (indexes doesn't change)
    return sortBy(
      listing.listing_photos.map((it, idx) => ({ ...it, id: `thumbnail-${idx}` })),
      'url',
    );
  }, [listing.listing_photos]);

  const [mainImgIdx, setMainImgIdx] = useState(() => {
    const defaultImageUrl =
      listing.listing_photos[0]?.thumbnail_urls?.['_760x480'] ||
      listing.exclusive_listing_info.local_area_map_image ||
      DEFAULT_LISTING_IMAGE;

    // Should match same photo used by GalleryGrid
    const firstPhotoCandidate = photos.findIndex((it) =>
      [it.url, ...Object.values(it.thumbnail_urls)].includes(defaultImageUrl),
    );

    // Naming this query param as lightbox to maintain backwards compatibility
    const photoViewerQueryParam = router.query.lightbox as string | undefined;

    if (!photoViewerQueryParam) {
      return firstPhotoCandidate;
    }

    const paramIndex = Number.parseInt(photoViewerQueryParam);

    if (isNaN(paramIndex)) return firstPhotoCandidate;
    if (paramIndex >= photos.length) return firstPhotoCandidate;
    return paramIndex;
  });

  const [isGalleryOpen, setIsGalleryOpen] = useState(() => {
    return (
      !isNaN(Number.parseInt((router.query.lightbox as string | undefined) ?? '')) &&
      hasListingPhotos
    );
  });

  const openGallery = hasListingPhotos ? () => setIsGalleryOpen(true) : undefined;
  const closeGallery = () => setIsGalleryOpen(false);

  useEffect(() => {
    // This gets the existing url path without query params
    const pathname = router.asPath.split('?')[0];
    const existingQuery = omit(router.query, ['slug', 'lightbox', 'region']);
    const query = { ...existingQuery, ...(isGalleryOpen ? { lightbox: mainImgIdx } : undefined) };

    router.replace({ pathname, query }, undefined, { shallow: true });
  }, [mainImgIdx, isGalleryOpen]);

  useEffect(() => {
    const returnTo = router.query.returnTo;
    if (
      returnTo &&
      !Array.isArray(returnTo) &&
      (REDIRECT_URLS_REGEX.test(returnTo) || isValidRelativeRedirectTo(returnTo))
    ) {
      setReturnHref(returnTo);
    }
  }, []);

  return (
    <section css={styles.propertyGallery}>
      <Box
        className="property-main-image"
        height={['270px', '270px', '420px', 'calc(100vh - 67px)']}
      >
        <Link
          display="flex"
          position="absolute"
          alignItems="center"
          justifyContent="center"
          backgroundColor="neutrals0"
          borderRadius="rounded"
          border="1px solid"
          borderColor="neutrals30"
          top={['1rem', '1rem', '1rem', '2rem']}
          left={['1rem', '1rem', '1rem', '2rem']}
          width={['2.5rem', '2.5rem', '2.5rem', '3.5rem']}
          height={['2.5rem', '2.5rem', '2.5rem', '3.5rem']}
          boxShadow="0px 5px 10px rgba(29, 71, 123, 0.04), 0px 1px 5px rgba(29, 71, 123, 0.12)"
          aria-label="Return to gallery"
          href={returnHref}
          analyticsName="cosmos-exclusives-property-gallery"
        >
          <ChevronLeftBold />
        </Link>
        {canShowMorePhotos && (
          <Button
            px={4}
            size="sm"
            width="auto"
            position="absolute"
            variant="secondary"
            aria-label="View all photos"
            right={['1rem', '1rem', '1rem', '2rem']}
            bottom={['1rem', '1rem', '1rem', '2rem']}
            onClick={openGallery}
            analyticsName="cosmos-exclusives-property-gallery-view-all-photos"
          >
            View all photos
          </Button>
        )}
        <Box
          h="100%"
          w="100%"
          border="0"
          padding="0"
          as="button"
          outline="none"
          cursor="pointer"
          aria-label="View all photos"
          onClick={openGallery}
        >
          <Image
            alt="Main Gallery"
            src={
              photos[mainImgIdx]?.thumbnail_urls?.['medium'] ||
              listing.exclusive_listing_info.local_area_map_image ||
              DEFAULT_LISTING_IMAGE
            }
          />
        </Box>
      </Box>

      <PhotoViewer
        open={isGalleryOpen}
        onClose={closeGallery}
        initialIndex={mainImgIdx}
        photos={photos}
        onActiveIndexChanged={(idx) => {
          if (isGalleryOpen) setMainImgIdx(idx);
        }}
        header={{
          title: getListingAddressHumanReadable(listing),
          subtitle: listing.listing_price_cents
            ? formatCurrency(listing.listing_price_cents / 100)
            : undefined,
        }}
        ctaOptions={{
          text: 'Self tour instantly',
          ariaLabel: 'Begin scheduling a tour',
          analyticsName: 'cosmos-exclusives-photo-viewer-self-tour-cta',
          shouldHide: isSellerOfferExperiment,
          onClick: () => {
            setIsGalleryOpen(false);
            onSelfTourClick();
          },
        }}
      />
    </section>
  );
};

export default PropertyGallery;
